import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import "./Chatbot.css";
import { FiShoppingCart, FiPackage, FiTag, FiCheckCircle, FiClock, FiTruck } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const API_URL = "https://pyate-backend-production-9749.up.railway.app/api/chatbot";

// ─────────────────────────────────────────────────────────
// Image with skeleton + error fallback
// ─────────────────────────────────────────────────────────
const LazyImage = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  if (!src || errored) {
    return (
      <div style={{
        width: "100%", height: 130, borderRadius: 10,
        background: "#e8f4fd", display: "flex",
        alignItems: "center", justifyContent: "center",
        fontSize: 28, color: "#93c5e8", margin: "6px 0",
      }}>📦</div>
    );
  }

  return (
    <>
      {!loaded && (
        <div className="skel" style={{
          width: "100%", height: 130, borderRadius: 10, margin: "6px 0",
        }} />
      )}
      <img
        src={src}
        alt={alt || "Product"}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
        style={{
          display: loaded ? "block" : "none",
          width: "100%", height: 130,
          objectFit: "contain", borderRadius: 10,
          margin: "6px 0 4px",
          border: "1px solid #cce3f5",
        }}
      />
    </>
  );
};

// ─────────────────────────────────────────────────────────
// Notification-style Product Card
// ─────────────────────────────────────────────────────────
const NotifProductCard = ({ card, onAddToCart }) => {
  const navigate = useNavigate();
  const hasDiscount = card.discount && parseFloat(card.discount) > 0;

  return (
    <div className="notif-cb-card" onClick={() => {
      // Mirror Shop.js: always navigate to /store/:pageid (the seller's store page)
      const dest = card.pageid || card.id;
      navigate(`/store/${dest}`);
    }}>
      <div className="notif-cb-avatar">
        {card.image_url ? (
          <img src={card.image_url} alt={card.name} className="notif-cb-img"
            onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="notif-cb-img-placeholder"><FiTag size={20} /></div>
        )}
      </div>
      <div className="notif-cb-content">
        <div className="notif-cb-name">{card.name}</div>
        {card.category && <div className="notif-cb-sub">{card.category}</div>}
        {card.description && (
          <div className="notif-cb-desc">
            {card.description.length > 50 ? card.description.substring(0, 50) + "…" : card.description}
          </div>
        )}
        <div className="notif-cb-price-row">
          {card.price && <span className="notif-cb-price">{card.price}</span>}
          {hasDiscount && card.mrp && <span className="notif-cb-mrp">{card.mrp}</span>}
          {hasDiscount && <span className="notif-cb-discount">{parseFloat(card.discount).toFixed(0)}% off</span>}
        </div>
      </div>
      <button
        className="notif-cb-action-btn"
        title="Add to Cart"
        onClick={e => { e.stopPropagation(); onAddToCart && onAddToCart(card); }}
      >
        <FiShoppingCart size={13} />
        <span>Add</span>
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Notification-style Cart Card
// ─────────────────────────────────────────────────────────
const NotifCartCard = ({ card }) => (
  <div className="notif-cb-card">
    <div className="notif-cb-avatar">
      {card.image_url ? (
        <img src={card.image_url} alt={card.name} className="notif-cb-img"
          onError={e => { e.target.style.display = "none"; }} />
      ) : (
        <div className="notif-cb-img-placeholder"><FiShoppingCart size={20} /></div>
      )}
    </div>
    <div className="notif-cb-content">
      <div className="notif-cb-name">{card.name}</div>
      {card.category && <div className="notif-cb-sub">{card.category}</div>}
      <div className="notif-cb-price-row">
        <span className="notif-cb-price">{card.price}</span>
        <span className="notif-cb-qty">× {card.quantity}</span>
      </div>
    </div>
    <div className="notif-cb-badge notif-cb-badge-cart">
      <FiShoppingCart size={11} /><span>In Cart</span>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────
// Notification-style Order Card
// ─────────────────────────────────────────────────────────
const NotifOrderCard = ({ card }) => {
  const navigate = useNavigate();
  const s = (card.status || "").toLowerCase();
  const StatusIcon =
    s === "delivered"                      ? FiCheckCircle :
    s === "shipped" || s === "out for delivery" ? FiTruck   : FiClock;

  const badgeClass =
    s === "delivered" ? "notif-cb-badge-delivered" :
    s === "shipped"   ? "notif-cb-badge-shipped"   : "notif-cb-badge-pending";

  return (
    <div className="notif-cb-card" onClick={() => navigate("/orders")}>
      <div className="notif-cb-avatar">
        {card.image_url ? (
          <img src={card.image_url} alt={card.name} className="notif-cb-img"
            onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <div className="notif-cb-img-placeholder"><FiPackage size={20} /></div>
        )}
      </div>
      <div className="notif-cb-content">
        <div className="notif-cb-name">{card.name}</div>
        <div className="notif-cb-sub">Order #{card.id}</div>
        <div className="notif-cb-price-row">
          <span className="notif-cb-price">{card.total}</span>
          {card.date && <span className="notif-cb-date">{card.date}</span>}
        </div>
      </div>
      <div className={`notif-cb-badge ${badgeClass}`}>
        <StatusIcon size={11} /><span>{card.status || "Pending"}</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Smart bot message renderer
// If response has cards → render notification-style cards
// Otherwise fall back to old markdown/product parsing
// ─────────────────────────────────────────────────────────
const BotMessage = ({ text, cards, cardType, onAddToCart }) => {

  // ── NEW: structured card response from updated backend ──
  if (cards && cards.length > 0) {
    return (
      <div>
        {text && <div style={{ marginBottom: 8, fontSize: 13, color: "#1e7ab8", fontWeight: 600 }}>{text}</div>}
        <div className="notif-cb-cards-list">
          {cards.map((card, idx) => {
            if (cardType === "products") return <NotifProductCard key={card.id || idx} card={card} onAddToCart={onAddToCart} />;
            if (cardType === "cart")     return <NotifCartCard    key={card.id || idx} card={card} />;
            if (cardType === "orders")  return <NotifOrderCard   key={card.id || idx} card={card} />;
            return null;
          })}
        </div>
      </div>
    );
  }

  // ── OLD FALLBACK: markdown-based parsing (unchanged) ──
  const sections = text.split(/\n---\n/).map(s => s.trim()).filter(Boolean);
  const isCart = /your cart|🛒/i.test(text);

  if (sections.length > 1 && text.includes("![")) {
    if (isCart) {
      const cartBlocks = sections.filter(b => !b.startsWith("###") && !/\*\*Total:/i.test(b));
      const totalMatch = text.match(/\*\*Total:\s*([₹$][\d,.]+)\*\*/);
      return (
        <div>
          <strong style={{ fontSize: 11, color: "#1e7ab8", textTransform: "uppercase", letterSpacing: ".5px" }}>
            🛒 Your Cart
          </strong>
          <div style={{ marginTop: 8 }}>
            {cartBlocks.map((b, i) => <CartItem key={i} block={b} />)}
          </div>
          {totalMatch && (
            <div className="cart-total">
              <span>Total</span>
              <span className="cart-total-amount">{totalMatch[1]}</span>
            </div>
          )}
        </div>
      );
    }
    const [headerBlock, ...productBlocks] = sections;
    const cleanHeader = headerBlock.replace(/!\[.*?\]\(.*?\)/g, "").trim();
    return (
      <div>
        {cleanHeader && <div style={{ marginBottom: 8 }}><ReactMarkdown>{cleanHeader}</ReactMarkdown></div>}
        {productBlocks.map((b, i) => <ProductCard key={i} block={b} />)}
      </div>
    );
  }

  return <ReactMarkdown>{text}</ReactMarkdown>;
};

// ─────────────────────────────────────────────────────────
// Old markdown-based ProductCard (kept as fallback)
// ─────────────────────────────────────────────────────────
const ProductCard = ({ block }) => {
  const nameMatch     = block.match(/^\*\*(.+?)\*\*/m);
  const imgMatch      = block.match(/!\[([^\]]*)\]\(([^)]+)\)/);
  const priceMatch    = block.match(/\*\*([₹$][\d,.]+)\*\*/);
  const mrpMatch      = block.match(/~~([₹$][\d,.]+)~~/);
  const discountMatch = block.match(/\((\d+(?:\.\d+)?)%\s*off\)/i);
  const categoryMatch = block.match(/\|\s*(.+?)(?:\n|$)/);
  const descMatch     = block.match(/\(.*?off\)[^\n]*\n([\s\S]+)/i)
                     || block.match(/\*\*[₹$][\d,.]+\*\*[^\n]*\n([\s\S]+)/);

  const name     = nameMatch     ? nameMatch[1].trim()                      : null;
  const imageUrl = imgMatch      ? imgMatch[2].trim()                       : null;
  const price    = priceMatch    ? priceMatch[1].trim()                     : null;
  const mrp      = mrpMatch      ? mrpMatch[1].trim()                       : null;
  const discount = discountMatch ? parseFloat(discountMatch[1]).toFixed(0)  : null;
  const category = categoryMatch ? categoryMatch[1].trim()                  : null;
  const desc     = descMatch     ? descMatch[1].trim()                      : null;

  if (!name) return null;

  return (
    <div className="pyate-product-card">
      <div className="pcard-img-wrap">
        <LazyImage src={imageUrl} alt={name} />
      </div>
      <div className="pcard-body">
        {category && <div className="pcard-category">{category}</div>}
        <div className="pcard-name">{name}</div>
        {desc && <div className="pcard-desc">{desc}</div>}
        <div className="pcard-price-row">
          {price    && <span className="pcard-price">{price}</span>}
          {mrp && discount && <span className="pcard-mrp">{mrp}</span>}
          {discount && <span className="pcard-badge">{discount}% OFF</span>}
        </div>
      </div>
    </div>
  );
};

// Old markdown-based CartItem (kept as fallback)
const CartItem = ({ block }) => {
  const nameMatch  = block.match(/^\*\*(.+?)\*\*/m);
  const imgMatch   = block.match(/!\[([^\]]*)\]\(([^)]+)\)/);
  const qtyMatch   = block.match(/Qty:\s*(\d+)/i);
  const priceMatch = block.match(/×\s*([₹$][\d,.]+)/i) || block.match(/([₹$][\d,.]+)/);

  const name  = nameMatch  ? nameMatch[1].trim()  : "Item";
  const img   = imgMatch   ? imgMatch[2].trim()   : null;
  const qty   = qtyMatch   ? qtyMatch[1].trim()   : "1";
  const price = priceMatch ? priceMatch[1].trim() : null;

  return (
    <div className="pyate-cart-item">
      <div className="ci-img">
        {img ? <img src={img} alt={name} onError={e => { e.target.style.display = "none"; }} /> : "🛍️"}
      </div>
      <div className="ci-info">
        <div className="ci-name">{name}</div>
        <div className="ci-qty">Qty: {qty}</div>
      </div>
      {price && <div className="ci-price">{price}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// Status messages shown in status bar while loading
// ─────────────────────────────────────────────────────────
const STATUS_MAP = {
  view_cart:     "Fetching your cart…",
  list_products: "Loading products…",
  place_order:   "Placing your order…",
  add_to_cart:   "Adding to cart…",
  view_orders:   "Loading your orders…",
  default:       "Thinking…",
};

function guessIntent(text) {
  const t = text.toLowerCase();
  if (/cart|basket/.test(t) && /show|see|view|what|display|items/.test(t)) return "view_cart";
  if (/add.*(cart|basket)/.test(t)) return "add_to_cart";
  if (/\border\b|buy|purchase|place/.test(t)) return "place_order";
  if (/product|item|available|list|browse|shop/.test(t)) return "list_products";
  if (/order history|my orders|past order/.test(t)) return "view_orders";
  return "default";
}

// ─────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────
const Chatbot = () => {
  const [isOpen,   setIsOpen]   = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState("");

  const chatEndRef = useRef(null);
  const inputRef   = useRef(null);

  const API_URL = "https://pyate-backend-production-9749.up.railway.app/api/chatbot";

  // 👋 Personalized greeting on load
  useEffect(() => {
    if (!isOpen || messages.length > 0) return;

    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const userName =
      storedUser.name ||
      storedUser.username ||
      storedUser.first_name ||
      storedUser.email?.split("@")[0] ||
      "there";

    setMessages([{
      id:   Date.now(),
      from: "bot",
      text: `👋 Hi **${userName}**, welcome to **Pyate Store**!\n\nI can help you browse products, manage your cart, or answer questions. What can I do for you?`,
    }]);
  }, [isOpen]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const getItemIdFromUrl = () => {
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    const productSegments = ["item", "items", "product", "products"];
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (productSegments.includes(pathParts[i].toLowerCase())) {
        const maybeId = pathParts[i + 1];
        if (/^\d+$/.test(maybeId)) return maybeId;
      }
    }
    return null;
  };

  // Add to cart handler for product cards
  const handleAddToCart = async (card) => {
    try {
      const token = localStorage.getItem("token");
      const user  = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        "https://pyate-backend-production-f7ce.up.railway.app/api/cart_items",
        { item_id: card.id, product_name: card.name, quantity: 1, user_id: user.userid },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, {
        id: Date.now(), from: "bot",
        text: `✅ **${card.name}** added to your cart!`,
      }]);
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now(), from: "bot",
        text: "❌ Could not add to cart. Please try again.",
        isError: true,
      }]);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userText = input.trim();
    const intent   = guessIntent(userText);

    setMessages(prev => [...prev, { id: Date.now(), from: "user", text: userText }]);
    setInput("");
    setLoading(true);
    setStatus(STATUS_MAP[intent] || STATUS_MAP.default);

    try {
      const token = localStorage.getItem("token");
      const user  = JSON.parse(localStorage.getItem("user") || "{}");

      const uiContext = {
        store_name:     "Pyate Store",
        current_page:   window.location.pathname,
        logged_in_user: user.username || user.email || "Guest",
        user_id:        user.userid || null,
        item_id:        getItemIdFromUrl(),
      };

      const chatHistory = messages.slice(-10).map(m => ({
        role:    m.from === "user" ? "user" : "assistant",
        content: m.text,
      }));

      const res = await axios.post(
        API_URL,
        { query: userText, ui_context: uiContext, chat_history: chatHistory },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const botReply  = res.data.answer || "🤖 Sorry, I didn't quite catch that. Could you repeat?";
      const cards     = res.data.cards     || null;
      const cardType  = res.data.card_type || null;

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        from: "bot",
        text: botReply,
        cards,
        cardType,
      }]);

    } catch (err) {
      console.error(err);
      const errMsg = err.response?.status === 401
        ? "🔒 Please log in to use the assistant."
        : "⚠️ Couldn't connect right now. Please try again.";
      setMessages(prev => [...prev, {
        id: Date.now() + 1, from: "bot", text: errMsg, isError: true,
      }]);
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        className="chatbot-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
        title={isOpen ? "Close chat" : "Chat with Pyate Assistant"}
      >
        <span className="chatbot-button-icon" aria-hidden="true" translate="no">
  {isOpen ? "✕" : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )}
</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-container">

          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-left">
              <div className="chatbot-avatar" aria-hidden="true">🤖</div>
              <div className="chatbot-header-titles">
                <h4>Pyate Assistant</h4>
                <div className="chatbot-subtitle">Online · replies instantly</div>
              </div>
            </div>
            <button
              className="close-btn-chat"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Status bar */}
          {loading && status && (
            <div className="chatbot-status-bar" role="status" aria-live="polite">
              <span className="status-spinner" aria-hidden="true" />
              {status}
            </div>
          )}

          {/* Messages */}
          <div className="chatbot-messages" role="log" aria-live="polite">
            <div className="chat-divider">Today</div>

            {messages.map(msg => (
              <div
                key={msg.id}
                className={`message ${msg.from}${msg.isError ? " error-msg" : ""}`}
              >
                {msg.from === "bot"
                  ? <BotMessage
                      text={msg.text}
                      cards={msg.cards}
                      cardType={msg.cardType}
                      onAddToCart={handleAddToCart}
                    />
                  : msg.text
                }
              </div>
            ))}

            {/* Skeleton loader */}
            {loading && (
              <div className="message bot" style={{ minWidth: 180, padding: "12px 14px" }}>
                <div className="skel" style={{ height: 11, width: "90%", marginBottom: 8 }} />
                <div className="skel" style={{ height: 11, width: "70%", marginBottom: 8 }} />
                <div className="skel" style={{ height: 11, width: "48%" }} />
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input">
            <input
              ref={inputRef}
              type="text"
              value={input}
              placeholder="Ask me anything…"
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              aria-label="Message"
              maxLength={500}
            />
            <button
              onClick={sendMessage}
              className="chatbot-send"
              disabled={!input.trim() || loading}
              aria-label="Send message"
              title="Send"
            >
              <span aria-hidden="true">➤</span>
            </button>
          </div>

        </div>
      )}
    </>
  );
};

export default Chatbot;